import React from "react";
import { apiClient } from "../../lib/api";

// Broker Reconciliation — Magic Moment #5 for the finance controller.
// One question, one screen: "Did we bill for every completed trip?"
// If not, exactly which ones and how much money is on the table.

function fmtUsd(n) {
  const v = Number(n) || 0;
  return `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtPct(n) {
  const v = Number(n) || 0;
  return `${v.toFixed(1)}%`;
}
function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
}

const WINDOWS = [
  { d: 7, label: "7d" },
  { d: 30, label: "30d" },
  { d: 90, label: "90d" },
  { d: 365, label: "1y" },
];

export default function ReconciliationPanel() {
  const [days, setDays] = React.useState(30);
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  const load = React.useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const r = await apiClient.get(`/admin/reconciliation`, { params: { days } });
      const body = r.data;
      if (body?.ok === false) {
        throw new Error(body?.error?.message || "Server rejected the request");
      }
      setData(body?.data || body);
    } catch (e) {
      const msg = e?.response?.data?.error?.message
                || e?.response?.data?.detail
                || e?.message
                || "Request failed";
      setError(String(msg));
    } finally {
      setLoading(false);
    }
  }, [days]);

  React.useEffect(() => { load(); }, [load]);

  const summary = data?.summary || {};
  const bySource = data?.by_source || [];
  const leaks = data?.leak_trips || [];

  return (
    <section
      className="rounded-2xl border border-[#E2E0D8] bg-white p-5 mb-6 astat-fade-in"
      data-testid="reconciliation-panel"
    >
      {/* Header */}
      <header className="flex items-center justify-between gap-4 mb-4">
        <div>
          <div className="text-xs font-semibold tracking-[0.2em] text-forest/70 uppercase">
            Revenue Leak Detector
          </div>
          <h2 className="text-xl font-bold text-slate_navy" data-testid="reconciliation-title">
            Broker reconciliation · last {days} days
          </h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Every completed trip that isn&apos;t on an invoice is money you already earned but haven&apos;t billed.
          </p>
        </div>
        <div className="flex items-center gap-1 rounded-full border border-[#E2E0D8] p-1">
          {WINDOWS.map(w => (
            <button
              key={w.d}
              onClick={() => setDays(w.d)}
              data-testid={`recon-window-${w.d}`}
              className={
                "px-3 py-1 text-xs font-semibold rounded-full transition " +
                (days === w.d
                  ? "bg-forest text-white"
                  : "text-slate-600 hover:bg-forest/10")
              }
            >
              {w.label}
            </button>
          ))}
          <button
            onClick={load}
            data-testid="recon-refresh"
            className="ml-1 px-3 py-1 text-xs font-semibold text-slate-600 hover:text-forest"
          >
            ↻
          </button>
        </div>
      </header>

      {error && (
        <div className="mb-4 rounded-lg border border-rust/50 bg-rust/5 px-3 py-2 text-sm text-rust" data-testid="recon-error">
          {error}
        </div>
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <KpiCard
          label="Invoiceable trips"
          value={summary.invoiceable_trips ?? "—"}
          sub={
            summary.self_paid_trips
              ? `+ ${summary.self_paid_trips} self-paid`
              : undefined
          }
          testid="recon-kpi-completed"
        />
        <KpiCard
          label="Invoice coverage"
          value={loading ? "…" : fmtPct(summary.invoice_coverage_pct)}
          tone={summary.invoice_coverage_pct >= 95 ? "green" : summary.invoice_coverage_pct >= 80 ? "amber" : "rust"}
          testid="recon-kpi-coverage"
        />
        <KpiCard
          label="Uninvoiced trips"
          value={summary.uninvoiced_trips ?? "—"}
          tone={(summary.uninvoiced_trips || 0) > 0 ? "amber" : "green"}
          testid="recon-kpi-uninvoiced"
        />
        <KpiCard
          label="Revenue on the table"
          value={loading ? "…" : fmtUsd(summary.uninvoiced_revenue_usd)}
          tone={(summary.uninvoiced_revenue_usd || 0) > 0 ? "rust" : "green"}
          testid="recon-kpi-leak"
          emphasis
        />
      </div>

      {/* By source breakdown */}
      <div className="mb-5">
        <div className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">
          By source · biggest leak first
        </div>
        <div className="overflow-x-auto rounded-lg border border-[#E2E0D8]">
          <table className="min-w-full text-sm" data-testid="recon-by-source-table">
            <thead className="bg-bone/60 text-slate-600">
              <tr>
                <th className="text-left px-3 py-2">Broker / source</th>
                <th className="text-right px-3 py-2">Invoiceable</th>
                <th className="text-right px-3 py-2">Invoiced</th>
                <th className="text-right px-3 py-2">Uninvoiced</th>
                <th className="text-right px-3 py-2">Self-paid</th>
                <th className="text-right px-3 py-2">Gross captured</th>
                <th className="text-right px-3 py-2">On the table</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="text-center py-6 text-slate-400">Loading…</td></tr>
              )}
              {!loading && bySource.length === 0 && (
                <tr><td colSpan={7} className="text-center py-6 text-slate-400">No trips in window</td></tr>
              )}
              {bySource.map((s) => (
                <tr key={`${s.source}::${s.broker}`} className="border-t border-[#E2E0D8]">
                  <td className="px-3 py-2">
                    <div className="font-semibold text-slate_navy">{s.broker}</div>
                    <div className="text-xs text-slate-500">{s.source}</div>
                  </td>
                  <td className="text-right px-3 py-2">{s.invoiceable ?? 0}</td>
                  <td className="text-right px-3 py-2">{s.invoiced}</td>
                  <td className={"text-right px-3 py-2 " + (s.uninvoiced > 0 ? "text-rust font-semibold" : "text-slate-500")}>
                    {s.uninvoiced}
                  </td>
                  <td className="text-right px-3 py-2 text-slate-400">{s.self_paid ?? 0}</td>
                  <td className="text-right px-3 py-2">{fmtUsd(s.gross_captured_usd)}</td>
                  <td className={"text-right px-3 py-2 " + (s.uninvoiced_revenue_usd > 0 ? "text-rust font-semibold" : "text-slate-500")}>
                    {fmtUsd(s.uninvoiced_revenue_usd)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Leak drill-down */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-semibold text-slate-600 uppercase tracking-wider">
            Trips not on any invoice yet
          </div>
          {leaks.length > 0 && (
            <div className="text-xs text-slate-500">
              Showing {leaks.length} · sorted newest first
            </div>
          )}
        </div>
        <div className="overflow-x-auto rounded-lg border border-[#E2E0D8]">
          <table className="min-w-full text-sm" data-testid="recon-leak-table">
            <thead className="bg-bone/60 text-slate-600">
              <tr>
                <th className="text-left px-3 py-2">Trip</th>
                <th className="text-left px-3 py-2">Broker</th>
                <th className="text-left px-3 py-2">Rider</th>
                <th className="text-left px-3 py-2">Completed</th>
                <th className="text-right px-3 py-2">Fare</th>
                <th className="text-left px-3 py-2">Payment</th>
                <th className="text-right px-3 py-2">Proof</th>
              </tr>
            </thead>
            <tbody>
              {!loading && leaks.length === 0 && (
                <tr><td colSpan={7} className="text-center py-6 text-slate-400">
                  100% invoice coverage in window. Well done.
                </td></tr>
              )}
              {leaks.map((t) => (
                <tr key={t.trip_id} className="border-t border-[#E2E0D8]" data-testid={`recon-leak-row-${t.trip_id}`}>
                  <td className="px-3 py-2 font-mono text-xs">
                    {t.trip_id?.slice(0, 8)}…
                  </td>
                  <td className="px-3 py-2">
                    <div className="font-semibold text-slate_navy">{t.broker}</div>
                    <div className="text-xs text-slate-500 truncate max-w-[240px]">{t.dropoff}</div>
                  </td>
                  <td className="px-3 py-2">{t.rider_name || "—"}</td>
                  <td className="px-3 py-2 text-slate-500">{fmtDate(t.completed_at)}</td>
                  <td className="px-3 py-2 text-right font-semibold text-slate_navy">
                    {fmtUsd(t.fare_amount_usd)}
                  </td>
                  <td className="px-3 py-2">
                    <StatusPill value={t.payment_status} />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <a
                      href={`/api/admin/trips/${t.trip_id}/compliance-packet`}
                      target="_blank" rel="noreferrer"
                      data-testid={`recon-leak-packet-${t.trip_id}`}
                      className="text-xs font-bold text-forest hover:underline"
                    >Proof ↗</a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function KpiCard({ label, value, tone = "slate", testid, emphasis = false, sub }) {
  const toneClasses = {
    slate: "border-[#E2E0D8] bg-bone/40 text-slate_navy",
    green: "border-forest/25 bg-forest/5 text-forest",
    amber: "border-amber-400/40 bg-amber-50 text-amber-700",
    rust:  "border-rust/40 bg-rust/5 text-rust",
  }[tone] || "border-[#E2E0D8] bg-bone/40 text-slate_navy";
  return (
    <div className={"rounded-xl border p-4 " + toneClasses} data-testid={testid}>
      <div className="text-[11px] font-semibold uppercase tracking-wider opacity-75">{label}</div>
      <div className={"mt-1 font-bold " + (emphasis ? "text-2xl" : "text-xl")}>{value}</div>
      {sub && <div className="mt-0.5 text-[11px] opacity-70">{sub}</div>}
    </div>
  );
}

function StatusPill({ value }) {
  const v = (value || "").toLowerCase();
  const tone = v === "captured" ? "bg-forest/10 text-forest"
             : v === "refunded" ? "bg-slate-200 text-slate-600"
             : v === "authorized" ? "bg-amber-50 text-amber-700"
             : "bg-slate-100 text-slate-500";
  return (
    <span className={"inline-block px-2 py-0.5 rounded-full text-xs font-semibold " + tone}>
      {v || "—"}
    </span>
  );
}
